import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock, ShieldAlert, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
  LoadingState,
  Select,
} from '@/components/ui/primitives';
import { ConfirmButton } from '@/components/ui/overlays';
import { formatRelative } from '@/lib/utils';
import { MIN_PASSWORD_LENGTH } from '@shared/schemas.js';
import { USER_ROLES, type UserRole } from '@shared/enums.js';

/**
 * Settings → Access.
 *
 * The controls the remote-access runbook tells the owner to use. Before this
 * existed the runbook said "use the enforcement control (admin-only)" and no
 * such control was in the client — the APIs were all there, but the only way to
 * reach them was a hand-written HTTP request. A runbook that points at a button
 * nobody can find is worse than no runbook.
 *
 * Scope is deliberately the pilot: one owner, one VA. There is no group model,
 * no permission editor and no invitation flow, because there is no second
 * organisation to model. Assigning actual leads stays on the Leads page; this
 * screen only shows how many each person holds.
 */

const ROLE_LABELS: Record<UserRole, string> = { admin: 'Administrator', va: 'Virtual assistant' };

export function AccessSettings(): JSX.Element {
  return (
    <div className="space-y-4">
      <EnforcementCard />
      <UsersCard />
      <RemoteAccessCard />
    </div>
  );
}

function RemoteAccessCard(): JSX.Element {
  return (
    <Card className="max-w-3xl border-primary/15 bg-primary/[0.035]">
      <CardHeader>
        <CardTitle>Remote access</CardTitle>
        <Badge variant="outline">Configured outside LeadForge</Badge>
      </CardHeader>
      <CardBody className="space-y-2">
        <p className="text-xs leading-relaxed text-muted-foreground">
          LeadForge listens on <span className="font-mono text-foreground">127.0.0.1</span> only. Remote access can be provided through Tailscale Serve.
          LeadForge does not configure or monitor Tailscale, Funnel, device sharing, or tailnet access.
        </p>
        <p className="text-2xs text-muted-foreground">
          Use the remote-access runbook for setup and verification. No fake connection status is shown here because LeadForge cannot observe Tailscale state.
        </p>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Authentication enforcement
// ---------------------------------------------------------------------------

/**
 * The switch that decides whether LeadForge asks anyone who they are.
 *
 * Both directions are consequential and neither is obvious from a toggle, so
 * each is a deliberate confirmation that states the consequence in plain words
 * before it happens. Turning it ON stops anonymous access, which is what makes
 * remote exposure survivable; turning it OFF hands the whole CRM to anyone who
 * can reach the port.
 */
function EnforcementCard(): JSX.Element {
  const queryClient = useQueryClient();
  const { authEnabled, refresh } = useAuth();
  const users = useQuery({ queryKey: ['users'], queryFn: api.listUsers });

  const activeAdmins =
    users.data?.users.filter((u) => u.role === 'admin' && u.isActive).length ?? 0;

  const change = useMutation({
    mutationFn: (enabled: boolean) => api.setAuthEnforcement(enabled),
    onSuccess: async (result) => {
      await refresh();
      await queryClient.invalidateQueries();
      toast.success(
        result.authEnabled
          ? 'Authentication is now required.'
          : 'Authentication is off. Anyone who can reach LeadForge can use it.',
        { duration: 10_000 },
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle>Authentication</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Current state</span>
          {authEnabled ? (
            <Badge variant="success">Required — everyone must sign in</Badge>
          ) : (
            <Badge variant="warning">Off — LeadForge does not ask who you are</Badge>
          )}
        </div>

        {authEnabled ? (
          <>
            <p className="text-xs text-muted-foreground">
              Turning this off means <strong>anyone who can reach LeadForge can use all of it</strong>
              , with no password. Network restrictions such as Tailscale may still apply, but the
              application itself will stop checking. Stop Tailscale Serve before you do this.
            </p>
            <ConfirmButton
              title="Turn authentication off?"
              description="Anyone who can reach LeadForge will be able to read and change everything in it, with no password."
              confirmLabel="Yes, turn it off"
              onConfirm={() => change.mutate(false)}
            >
              <Button variant="outline" size="sm">
                <ShieldAlert className="h-3.5 w-3.5" />
                Disable authentication
              </Button>
            </ConfirmButton>
          </>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              Turning this on stops anonymous access immediately. Everyone — including you — will
              need a LeadForge username and password, and any browser currently using LeadForge
              without signing in will be asked to. Create your own administrator account first.
            </p>
            {activeAdmins === 0 && (
              <p className="text-xs text-danger">
                There is no active administrator account. The server will refuse to enable
                authentication until one exists — otherwise nobody could sign in.
              </p>
            )}
            <ConfirmButton
              title="Require everyone to sign in?"
              description="Anonymous access to the CRM stops immediately. You and the VA will each need a LeadForge username and password."
              confirmLabel="Yes, require sign-in"
              destructive={false}
              onConfirm={() => change.mutate(true)}
            >
              <Button size="sm" disabled={activeAdmins === 0}>
                <Lock className="h-3.5 w-3.5" />
                Enable authentication
              </Button>
            </ConfirmButton>
          </>
        )}

        <p className="text-2xs text-muted-foreground">
          Locked out? At the LeadForge machine, run <code>npm run auth:disable</code>. That is the
          emergency recovery path and it deliberately requires physical access to the host.
        </p>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

function UsersCard(): JSX.Element {
  const users = useQuery({ queryKey: ['users'], queryFn: api.listUsers });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Users</CardTitle>
        <CreateUserForm />
      </CardHeader>
      <CardBody>
        {users.isLoading ? (
          <LoadingState />
        ) : !users.data?.users.length ? (
          <p className="text-xs text-muted-foreground">
            No accounts yet. Create your own administrator account before enabling authentication.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-1.5 pr-3 font-medium">Name</th>
                  <th className="py-1.5 pr-3 font-medium">Username</th>
                  <th className="py-1.5 pr-3 font-medium">Role</th>
                  <th className="py-1.5 pr-3 font-medium">State</th>
                  <th className="py-1.5 pr-3 font-medium">Assigned leads</th>
                  <th className="py-1.5 pr-3 font-medium">Last signed in</th>
                  <th className="py-1.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.data.users.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-2xs text-muted-foreground">
          Assigning leads happens on the Leads page. The count here is only how many active
          assignments each person currently holds.
        </p>
      </CardBody>
    </Card>
  );
}

type UserWithCounts = Awaited<ReturnType<typeof api.listUsers>>['users'][number];

function UserRow({ user }: { user: UserWithCounts }): JSX.Element {
  const queryClient = useQueryClient();
  const { user: me } = useAuth();
  const [resetting, setResetting] = React.useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const setActive = useMutation({
    mutationFn: (isActive: boolean) => api.setUserActive(user.id, isActive),
    onSuccess: (_, isActive) => {
      invalidate();
      toast.success(
        isActive
          ? `${user.displayName} can sign in again.`
          : `${user.displayName} is deactivated and every session of theirs has ended.`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <tr className="border-b border-border/60">
        <td className="py-1.5 pr-3">{user.displayName}</td>
        <td className="py-1.5 pr-3 font-mono text-2xs">{user.username}</td>
        <td className="py-1.5 pr-3">{ROLE_LABELS[user.role]}</td>
        <td className="py-1.5 pr-3">
          <div className="flex flex-wrap items-center gap-1">
            {user.isActive ? (
              <Badge variant="success">Active</Badge>
            ) : (
              <Badge>Inactive</Badge>
            )}
            {user.mustChangePassword && <Badge variant="warning">Temporary password</Badge>}
          </div>
        </td>
        <td className="py-1.5 pr-3 tabular-nums">{user.activeAssignments}</td>
        <td className="py-1.5 pr-3 text-muted-foreground">
          {user.lastLoginAt ? formatRelative(user.lastLoginAt) : 'never'}
        </td>
        <td className="py-1.5">
          <div className="flex flex-wrap gap-1.5">
            <Button variant="outline" size="sm" onClick={() => setResetting((v) => !v)}>
              <KeyRound className="h-3.5 w-3.5" />
              Reset password
            </Button>
            {user.isActive ? (
              <ConfirmButton
                title={`Deactivate ${user.displayName}?`}
                description="They can no longer sign in, and every session of theirs ends immediately. Their assignments and call history are untouched."
                confirmLabel="Deactivate"
                onConfirm={() => setActive.mutate(false)}
              >
                {/*
                  The server refuses to deactivate the last active administrator.
                  Disabling it for yourself only avoids offering an action that is
                  obviously self-defeating.
                */}
                <Button variant="outline" size="sm" disabled={user.id === me?.id}>
                  Deactivate
                </Button>
              </ConfirmButton>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setActive.mutate(true)}>
                Reactivate
              </Button>
            )}
          </div>
        </td>
      </tr>
      {resetting && (
        <tr className="border-b border-border/60 bg-muted/20">
          <td colSpan={7} className="px-2 py-2">
            <ResetPasswordForm user={user} onDone={() => setResetting(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Reset someone's password to a temporary one the admin types.
 *
 * No generator and no default. A default password is a password that exists in
 * a document somewhere forever; a generated one has to be read off a screen and
 * relayed, which is the same problem with extra steps. The admin chooses one,
 * says it to the person once, and the server forces a change at first sign-in
 * so the admin never knows the real one.
 */
function ResetPasswordForm({
  user,
  onDone,
}: {
  user: UserWithCounts;
  onDone: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [password, setPassword] = React.useState('');

  const reset = useMutation({
    mutationFn: () => api.resetUserPassword(user.id, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setPassword('');
      onDone();
      toast.success(
        `Temporary password set for ${user.displayName}. Their sessions have ended and they must choose a new password at next sign-in.`,
        { duration: 10_000 },
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        reset.mutate();
      }}
    >
      <Field
        label={`Temporary password for ${user.displayName}`}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters. They will be required to change it.`}
        className="min-w-[18rem] flex-1"
      >
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
      </Field>
      <Button type="submit" size="sm" loading={reset.isPending} disabled={password.length < MIN_PASSWORD_LENGTH}>
        Set temporary password
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onDone}>
        Cancel
      </Button>
    </form>
  );
}

function CreateUserForm(): JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    username: '',
    displayName: '',
    role: 'va' as UserRole,
    password: '',
    mustChangePassword: true,
  });

  const create = useMutation({
    mutationFn: () => api.createUser(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setForm({ username: '', displayName: '', role: 'va', password: '', mustChangePassword: true });
      setOpen(false);
      toast.success('Account created. Give them the temporary password directly, not in writing.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)}>
        <UserPlus className="h-3.5 w-3.5" />
        Add user
      </Button>
    );
  }

  return (
    <form
      className="w-full space-y-3 rounded-xl border border-border bg-muted/25 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Display name">
          <Input
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            required
          />
        </Field>
        <Field label="Username" hint="Stored lower-case. Used only to sign in.">
          <Input
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            autoComplete="off"
            required
          />
        </Field>
        <Field label="Role">
          <Select
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
          >
            {USER_ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Temporary password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. Choose one; nothing is generated for you.`}
        >
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            autoComplete="new-password"
            required
          />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={form.mustChangePassword}
          onChange={(e) => setForm((f) => ({ ...f, mustChangePassword: e.target.checked }))}
        />
        Require a password change at first sign-in
      </label>
      <p className="text-2xs text-muted-foreground">
        Leave that ticked for anyone but yourself: it is what stops you from ever knowing their real
        password.
      </p>
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          loading={create.isPending}
          disabled={form.password.length < MIN_PASSWORD_LENGTH}
        >
          Create account
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
